import { Controller } from "@hotwired/stimulus"
import { detachPushUserOnLogout } from "./onesignal_device_controller"

/**
 * Logout Controller
 *
 * Handles user logout with proper push notification cleanup.
 *
 * ============================================================
 * CRITICAL: Push Notification Handling on Logout
 * ============================================================
 * 
 * On logout, we ONLY detach the user from OneSignal (externalUserId = null).
 * We do NOT call optOutPush.
 * 
 * Why optOutPush is FORBIDDEN on logout:
 * - optOutPush permanently disables push notifications for this device
 * - requestPushPermission() does NOT re-enable push after an optOut
 * - This causes notifications to stop working after logout → login
 * 
 * The correct behavior is:
 * - Detach user: The device is no longer associated with the logged-out user
 * - Keep subscription active: The next login can seamlessly re-attach
 * - On next login: handleLoginLifecycle() will re-attach the new user
 * 
 * optOutPush is ONLY allowed for:
 * - Account deletion (user will never log back in with this account)
 * - User explicitly disabling notifications in settings
 *
 * ============================================================
 *
 * Usage:
 *   <a href="/logout" 
 *      data-controller="logout"
 *      data-action="click->logout#handleLogout"
 *      data-turbo-method="delete">
 *     Log out
 *   </a>
 */
export default class extends Controller {
  static values = {
    confirmMessage: String
  }

  /**
   * Handle logout click
   * Detaches user from push notifications (without opting out) before proceeding with logout
   */
  async handleLogout(event) {
    // Store element reference before async operations
    // Use event.currentTarget if available, otherwise fall back to controller element
    const element = event?.currentTarget || this.element

    // If there's a confirmation and user cancels, stop
    if (this.hasConfirmMessageValue && this.confirmMessageValue) {
      if (!confirm(this.confirmMessageValue)) {
        if (event) event.preventDefault()
        return
      }
    }

    // Prevent default to handle async operations first
    if (event) event.preventDefault()

    // Validate element exists before proceeding
    if (!element) {
      console.error("[Logout] Cannot proceed: element is null")
      return
    }

    try {
      // Detach user from push notifications (does NOT opt out)
      // This clears the externalUserId but keeps the subscription active
      // So the next login can seamlessly re-attach and receive notifications
      await detachPushUserOnLogout()
    } catch (error) {
      // Don't block logout if detach fails
      console.error("[Logout] Push user detach failed:", error)
    }

    // Proceed with logout - submit the form or follow the link
    if (element.tagName === "FORM") {
      element.submit()
    } else if (element.tagName === "A") {
      // For turbo links, trigger the request manually
      const method = element.dataset.turboMethod || "get"
      
      if (method.toLowerCase() === "delete") {
        // Create and submit a form for DELETE request
        const form = document.createElement("form")
        form.method = "POST"
        form.action = element.href
        
        // Add method override
        const methodInput = document.createElement("input")
        methodInput.type = "hidden"
        methodInput.name = "_method"
        methodInput.value = "delete"
        form.appendChild(methodInput)
        
        // Add CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        if (csrfToken) {
          const csrfInput = document.createElement("input")
          csrfInput.type = "hidden"
          csrfInput.name = "authenticity_token"
          csrfInput.value = csrfToken
          form.appendChild(csrfInput)
        }
        
        document.body.appendChild(form)
        form.submit()
      } else {
        window.location.href = element.href
      }
    } else if (element.tagName === "BUTTON") {
      // Find parent form and submit
      const form = element.closest("form")
      if (form) {
        form.submit()
      }
    }
  }
}